<template>
  <div>
    <!-- Identity -->
    <section class="nerv-section">
      <div class="ops-head">
        <span class="nerv-section-label">Sec 02 // Operator</span>
        <span class="ops-line">
          <span class="nerv-status nerv-lcd">Link active</span>
          <span class="nerv-caption">{{ linkLine }}</span>
        </span>
      </div>
      <div class="nerv-panel nerv-bracket ident">
        <div class="ident-main">
          <h1 class="nerv-heading nerv-heading-xl">Daniil Demidov</h1>
          <p class="nerv-lede">AI Engineer &middot; Applied AI and agent infrastructure</p>
          <a class="nerv-btn ident-resume" href="/Daniil-Demidov-Resume.pdf" download>
            Download resume (PDF)
          </a>
          <p class="nerv-microcopy ident-blurb">
            Ships production LLM and agent systems end to end: prompt design, tool calling, evals,
            integration and deployment. Eleven years building multi-tenant SaaS across TypeScript,
            .NET, Go and Python, including Fraud and Security engineering at a digital banking
            platform serving 17M+ end users.
          </p>
        </div>
        <dl class="ident-data">
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Location</dt>
            <dd class="nerv-datarow-value">Plano, Texas (Dallas metro)</dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Phone</dt>
            <dd class="nerv-datarow-value">
              <a href="tel:+12149457067" class="link">214-945-7067</a>
            </dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Email</dt>
            <dd class="nerv-datarow-value">
              <a href="mailto:daniil@dev1consulting.com" class="link">daniil@dev1consulting.com</a>
            </dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">LinkedIn</dt>
            <dd class="nerv-datarow-value">
              <a href="https://www.linkedin.com/in/DaniilDemidov" target="_blank" rel="noopener" class="link">
                /in/DaniilDemidov
              </a>
            </dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">GitHub</dt>
            <dd class="nerv-datarow-value">
              <a href="https://github.com/iCodeWebApps" target="_blank" rel="noopener" class="link">
                /iCodeWebApps
              </a>
            </dd>
          </div>
        </dl>
      </div>
    </section>

    <!-- Summary -->
    <section class="nerv-section">
      <span class="nerv-section-label">Sec 02.1 // Summary</span>
      <div class="summary">
        <p v-for="line in summary" :key="line" class="nerv-body">{{ line }}</p>
      </div>
    </section>

    <!-- Expertise -->
    <section class="nerv-section">
      <span class="nerv-section-label">Sec 02.2 // Core expertise</span>
      <h2 class="nerv-heading nerv-heading-lg section-title">Technical skills</h2>
      <div class="skill-groups">
        <div v-for="group in skills" :key="group.group" class="skill-group">
          <span class="nerv-data-label">{{ group.group }}</span>
          <ul class="chips">
            <li v-for="item in group.items" :key="item" class="chip">{{ item }}</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Experience -->
    <section class="nerv-section">
      <span class="nerv-section-label">Sec 02.3 // Service record</span>
      <h2 class="nerv-heading nerv-heading-lg section-title">Professional experience</h2>

      <div class="record">
        <article v-for="(job, i) in experience" :key="job.company" class="nerv-panel job">
          <header class="job-head" @click="toggle(i)" role="button" tabindex="0" @keydown.enter="toggle(i)" @keydown.space.prevent="toggle(i)">
            <div>
              <span class="nerv-data-label">{{ job.duration }}</span>
              <h3 class="nerv-heading nerv-heading-md">{{ job.company }}</h3>
              <p class="nerv-caption">{{ job.position }} &middot; {{ job.location }}</p>
            </div>
            <span class="expand" :class="{ open: isOpen(i) }" aria-hidden="true"></span>
          </header>
          <ul v-show="isOpen(i)" class="job-body">
            <li v-for="line in job.description" :key="line" class="nerv-body-sm">{{ line }}</li>
          </ul>
        </article>
      </div>
    </section>

    <!-- Education -->
    <section class="nerv-section">
      <span class="nerv-section-label">Sec 02.4 // Education</span>
      <div class="nerv-panel nerv-bracket education">
        <h3 class="nerv-heading nerv-heading-md">Bachelor of Science in Mathematics &amp; Computer Science</h3>
        <p class="nerv-microcopy">Texas Tech University, Lubbock, TX</p>
      </div>
    </section>

    <!-- CTA -->
    <section class="nerv-section">
      <div class="about-cta">
        <a class="nerv-btn" href="/Daniil-Demidov-Resume.pdf" download>Download resume (PDF)</a>
        <router-link to="/contact" class="nerv-btn nerv-btn-ghost">Start a conversation</router-link>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { acquireTelemetry, releaseTelemetry, telemetry } from '@/composables/useTelemetry';

export default defineComponent({
  name: 'AboutPage',
  computed: {
    linkLine(): string {
      const parts: string[] = [];
      if (telemetry.engine && telemetry.platform) {
        parts.push(`${telemetry.engine} on ${telemetry.platform}`);
      }
      if (telemetry.timezone) parts.push(telemetry.timezone);
      parts.push(`t+ ${telemetry.uptime}`);
      return parts.join(' / ');
    },
  },
  data() {
    return {
      open: [0, 1, 2, 3, 4, 5] as number[],
      summary: [
        'AI engineer with eleven years of production experience, specializing in applied AI, enterprise integration and multi-tenant platform work.',
        'Builds and ships LLM and agent systems end to end, and stands up AI initiatives inside teams that have not run one before.',
        'Former consultant CTO. Comfortable owning a product surface and driving technical strategy with customers, not just writing the code.',
        'Built the donation-platform category twice: the NonProfitCore donation hub at Foundant, then DonatoTomato, rebuilt solo and running in production after the original was sunset.',
      ],
      skills: [
        {
          group: 'AI and agents',
          items: [
            'LLM application development', 'Tool calling', 'Prompt engineering', 'Evals',
            'MCP servers', 'Multi-agent orchestration', 'Agent observability',
            'Anthropic Claude API', 'OpenAI API',
          ],
        },
        {
          group: 'Languages',
          items: ['TypeScript', 'C# / .NET', 'Go', 'Python', 'SQL', 'PowerShell'],
        },
        {
          group: 'Backend and data',
          items: [
            'Node.js', 'REST APIs', 'PostgreSQL', 'SQL Server', 'MySQL', 'MongoDB',
            'Supabase (RLS)', 'RabbitMQ', 'Event-driven systems',
          ],
        },
        {
          group: 'Cloud and infrastructure',
          items: [
            'Azure Functions', 'Azure Queues', 'Azure Runbooks', 'AWS', 'Terraform',
            'Vercel', 'CI/CD', 'Microsoft Graph',
          ],
        },
        {
          group: 'Auth and security',
          items: [
            'OAuth2 / OIDC', 'SAML', 'TOTP / MFA', 'Multi-tenant isolation', 'Identity and fraud',
          ],
        },
      ],
      experience: [
        {
          company: 'Dev1 Consulting LLC',
          location: 'Plano, Texas',
          position: 'Founder / Full Stack Engineer (Applied AI)',
          duration: 'July 2024 to Present',
          description: [
            'DonatoTomato: built and shipped a multi-tenant nonprofit fundraising platform on Supabase and Stripe Connect direct-charge, with multi-tenant row-level security, TOTP MFA, webhook-driven payment flows and nightly reconciliation jobs. Companion WordPress plugin published on WordPress.org.',
            'Pinnacle Group: built HTTP-triggered Azure Functions automating employee on and offboarding for roughly five hires a week, saving two to four hours of manual IT work per employee. Orchestrated Microsoft Graph, Exchange, Workday and Freshservice APIs through PowerShell Azure Runbooks over Azure Queues on an eventual-consistency model.',
            'Henosis CRM: built phase one of a multi-tenant B2B CRM for alternative-asset service providers, with Nylas email and calendar integration, Supabase RLS and edge functions.',
            'Open-source agentic tooling: published claude-rpg on npm, a real-time visualizer for multi-agent Claude Code sessions.',
          ],
        },
        {
          company: 'Foundant',
          location: 'Bozeman, Montana (remote)',
          position: 'Software Engineer, Platform',
          duration: 'September 2022 to February 2024',
          description: [
            'Built a cloud-native multi-tenant grant management platform on Vue 2, Go 1.19, MySQL, Terraform and AWS.',
            'Built event-sourced library components with RabbitMQ retry mechanisms for asynchronous processing.',
            'Designed and built the donor-facing donation form and hub for NonProfitCore.',
          ],
        },
        {
          company: 'AltConnect',
          location: 'Dallas, Texas',
          position: 'Consultant CTO',
          duration: 'November 2021 to September 2022',
          description: [
            'Embedded with a private-equity software customer as fractional CTO. Drove an Integration API from requirements through production rollout and owned end-to-end technical strategy for the flagship product.',
            'Managed and mentored a remote engineering team.',
          ],
        },
        {
          company: 'Alkami',
          location: 'Plano, Texas',
          position: 'Senior Software Engineer, Fraud and Security',
          duration: 'August 2020 to November 2021',
          description: [
            'Built a provider-based authentication solution integrating external identity providers on VueJS, .NET Core 3.1 and SQL Server 2017.',
            'Hardened the identity surface area for a regulated digital banking platform serving 17M+ end users across credit unions and community banks.',
            'Engineered complex integration scenarios against banking core systems.',
          ],
        },
        {
          company: 'Lanvera',
          location: 'Coppell, Texas',
          position: 'Senior Software Engineer, Architecture',
          duration: 'July 2016 to August 2020',
          description: [
            'Designed and built a multi-tenant document delivery platform on Angular, .NET Framework 4.8 and SQL Server 2017, securing document access for 5M+ end users within the first year.',
            'Led an intern development team building a production-monitoring solution that surfaced system health and availability insights for support teams.',
          ],
        },
        {
          company: 'Ethos Group',
          location: 'Irving, Texas',
          position: 'Software Engineer, Business Services',
          duration: 'June 2015 to July 2016',
          description: [
            'Enhanced core business systems on .NET Framework 4.0, SQL Server 2012 and VB.NET, and maintained ASP.NET Web API integrations with third-party automotive data brokers.',
          ],
        },
      ],
    };
  },
  methods: {
    isOpen(i: number): boolean {
      return this.open.includes(i);
    },
    toggle(i: number) {
      const at = this.open.indexOf(i);
      if (at >= 0) this.open.splice(at, 1);
      else this.open.push(i);
    },
  },
  mounted() {
    acquireTelemetry();
  },
  beforeUnmount() {
    releaseTelemetry();
  },
});
</script>

<style scoped>
.ops-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.ops-line {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.ops-line .nerv-caption {
  font-variant-numeric: tabular-nums;
}

.ident {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 22rem);
  gap: clamp(1.5rem, 3vw, 3rem);
  align-items: start;
  margin-block-start: 0.75rem;
}

@media (max-width: 900px) {
  .ident {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ident-main h1 {
  margin-block-end: 0.5rem;
}

.link {
  color: var(--nerv-orange);
  border-bottom: 1px solid transparent;
  transition: border-color 0.15s linear;
}

.link:hover {
  border-bottom-color: var(--nerv-orange);
}

.summary {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-block-start: 0.75rem;
  padding-left: 1rem;
  border-left: 1px solid var(--line-strong);
}

.section-title {
  margin-block: 0.75rem 1.5rem;
}

/* A recruiter's first task is finding the resume. It sits above the fold on the
   page they land on, not only at the bottom. */
.ident-resume {
  margin-block-start: 1.25rem;
}

.ident-blurb {
  margin-block-start: 1rem;
  max-width: 60ch;
}

.skill-groups {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.skill-group {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding-block-end: 1.25rem;
  border-bottom: 1px solid var(--line-soft);
}

.skill-group:last-child {
  border-bottom: 0;
  padding-block-end: 0;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  list-style: none;
}

.chip {
  padding: 0.4rem 0.75rem;
  border: 1px solid rgba(255, 153, 0, 0.25);
  background: rgba(255, 153, 0, 0.05);
  color: var(--nerv-orange);
  font-family: var(--font-nerv-mono);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
}

.education h3 {
  margin-block-end: 0.5rem;
}

.about-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.record {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.job {
  padding: 0;
}

.job-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: clamp(0.9rem, 2vw, 1.25rem);
  cursor: pointer;
}

.job-head:hover {
  background: rgba(255, 153, 0, 0.04);
}

.job-head h3 {
  margin-block: 0.35rem 0.25rem;
}

.expand {
  position: relative;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

.expand::before,
.expand::after {
  content: '';
  position: absolute;
  inset: 0;
  margin: auto;
  background: var(--nerv-orange);
}

.expand::before { width: 14px; height: 1px; }
.expand::after { width: 1px; height: 14px; transition: transform 0.2s linear; }
.expand.open::after { transform: scaleY(0); }

.job-body {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0 clamp(0.9rem, 2vw, 1.25rem) clamp(0.9rem, 2vw, 1.25rem) 2rem;
  list-style: none;
}

.job-body li {
  position: relative;
}

.job-body li::before {
  content: '';
  position: absolute;
  left: -1rem;
  top: 0.5rem;
  width: 0.35rem;
  height: 0.35rem;
  background: var(--nerv-orange);
  opacity: 0.6;
}
</style>
